import { isNaN, sumBy } from 'lodash'
import moment from 'moment'

import {
  AICounterResponse,
  ControlType,
  FormActionCreateOrUpdateRequest,
  FormActionResponse,
  FormControlInfoRequest,
  FormControlResponse,
  FormControlType,
  FormCounterCreateOrUpdateRequest,
  FormTabsPanelCreateOrUpdateRequest,
  FormTabsPanelResponse,
  FormControlCreateOrUpdateRequest,
  FormControlOptionCreateOrUpdateRequest,
  FormControlDependencyType,
  FormControlDependencyResponse,
  FormControlValidationResponse,
  FormControlValidationType,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { ActionElement, ControlElement, DependencyType, IValidator } from '@collaborate-health/forms'
import { AICounterElement, TabsElement } from '@tabeeb/modules/forms/types'

import { FormControl } from '../types'

const getControlType = (control: FormControlResponse): ControlElement['type'] => {
  switch (control.ControlType) {
    case ControlType.Text:
      switch (control.Type) {
        case FormControlType.PhoneNumber:
          return 'phone'
        case FormControlType.BarcodeAndQRCode:
          return 'qrcode'
        case FormControlType.Email:
          return 'email'
        case FormControlType.BodyTemperature:
        default:
          return 'text'
      }
    case ControlType.Textarea:
      return 'textarea'
    case ControlType.Radiobuttonlist:
      return 'radiobuttonlist'
    case ControlType.Checkboxlist:
      return 'checkboxlist'
    case ControlType.Selectlist:
      return 'selectlist'
    case ControlType.Date:
      return 'date'
    case ControlType.DateTime:
      return 'datetime'
    default:
      throw new Error(`Unknown control type: ${control.ControlType}`)
  }
}

export const mapFormActionControlToElement = (
  action: FormActionResponse
): ActionElement<FormActionCreateOrUpdateRequest> => {
  return {
    id: action.Id.toString(),
    elementType: 'action',
    order: action.Order,
    name: action.Label?.trim()!,
    type: 'run-workflow',
    canBeDeleted: true,
    canBeReordered: true,
    canBeHidden: false,
    meta: {
      ...action,
      WorkflowId: action.WorkflowId!,
      Label: action.Label?.trim()!,
    },
  }
}

export const mapFormTabToElement = (
  tab: FormTabsPanelResponse,
  canChange: boolean = true
): TabsElement<FormTabsPanelCreateOrUpdateRequest> => {
  return {
    id: tab.Id.toString(),
    elementType: 'tabs',
    order: tab.Order,
    name: tab.Name?.trim(),
    type: 'default',
    canBeDeleted: canChange,
    canBeReordered: canChange,
    canBeHidden: false,
    meta: {
      Id: tab.Id,
      Name: tab.Name?.trim()!,
      Order: tab.Order,
      Tabs:
        tab.Tabs?.map((t) => ({
          Id: t.Id,
          Value: t.Value?.trim()!,
          Order: t.Order,
        })) ?? [],
    },
  }
}

export const mapFormCounterToElement = (
  counter: AICounterResponse
): AICounterElement<FormCounterCreateOrUpdateRequest> => {
  return {
    id: counter.Id.toString(),
    elementType: 'aiCounter',
    order: counter.Order,
    name: counter.Label?.trim()!,
    type: 'default',
    canBeDeleted: true,
    canBeReordered: true,
    canBeHidden: false,
    meta: {
      ...counter,
      Label: counter.Label!,
    },
  }
}

export const mapControlDependencyTypeToFormControlDependencyType = (
  type: DependencyType
): FormControlDependencyType => {
  switch (type) {
    case 'enable':
      return FormControlDependencyType.Enable
    case 'filterValue':
      return FormControlDependencyType.FilterValue
    case 'require':
      return FormControlDependencyType.Require
    case 'show':
      return FormControlDependencyType.Show
    case 'value':
    default:
      return FormControlDependencyType.Value
  }
}

export const mapFormControlDependencyTypeToControlDependencyType = (
  type: FormControlDependencyType
): DependencyType => {
  switch (type) {
    case FormControlDependencyType.Enable:
      return 'enable'
    case FormControlDependencyType.FilterValue:
      return 'filterValue'
    case FormControlDependencyType.Require:
      return 'require'
    case FormControlDependencyType.Show:
      return 'show'
    case FormControlDependencyType.Value:
    default:
      return 'value'
  }
}

const getNumberOrDefault = (formValue: string | number | null | undefined) => {
  if (typeof formValue === 'number') {
    return formValue
  }

  const parsedValue = Number(formValue)
  if (isNaN(parsedValue)) {
    return 0
  }

  return parsedValue
}

export const mapValidationToControlValidator = (
  control: FormControlResponse,
  validation: FormControlValidationResponse,
  allControls: FormControlResponse[]
): IValidator => {
  const dependentControlsNames = validation.ControlIds?.map((id) => allControls.find((c) => c.Id === id)?.Label)
    .filter(Boolean)
    .join(', ')

  if (validation.Type === FormControlValidationType.EqualsToSum) {
    const sumValidator: IValidator = {
      validate(value, formValues) {
        if (value === null || value === undefined || value === '') {
          return { isValid: true }
        }

        const parsedValue = getNumberOrDefault(value as string | undefined)
        const sum = sumBy(
          validation.ControlIds?.map((id) => id.toString()).map((id) => getNumberOrDefault(formValues[id] as string))
        )

        if (parsedValue !== sum) {
          return {
            isValid: false,
            error: `Value must be equal to the sum of ${dependentControlsNames}`,
          }
        }

        return { isValid: true }
      },
    }

    return sumValidator
  }

  if (validation.Type === FormControlValidationType.GreaterThan) {
    const greaterThanValidator: IValidator = {
      validate(value, formValues) {
        if (value === null || value === undefined || value === '') {
          return { isValid: true }
        }

        const comparedValue = formValues[validation.ControlIds?.[0]?.toString() || ''] as string | undefined | null
        if (comparedValue === null || comparedValue === undefined || comparedValue === '') {
          return { isValid: true }
        }

        if (control.ControlType === ControlType.Date || control.ControlType === ControlType.DateTime) {
          const currentFieldDate = moment(value)
          const comparedFieldDate = moment(comparedValue)

          if (!currentFieldDate.isAfter(comparedFieldDate)) {
            return {
              isValid: false,
              error: `Value must be greater than ${dependentControlsNames}`,
            }
          }
        }

        return { isValid: true }
      },
    }

    return greaterThanValidator
  }

  throw new Error(`Unknown validation type: ${validation.Type}`)
}

export const mapFormControlToControlElement = (
  control: FormControlResponse,
  allControls: FormControlResponse[]
): ControlElement<FormControlCreateOrUpdateRequest> => {
  const element: ControlElement<FormControlCreateOrUpdateRequest> = {
    id: control.Id.toString(),
    elementType: 'control',
    order: control.Order,
    name: control.Label?.trim()!,
    tooltip: control.Tooltip?.trim() ?? undefined,
    isRequired: control.IsRequired,
    type: getControlType(control),
    displayLabel: false,
    size: 'small',
    options: {
      totalCount: control.Options?.length || 0,
      values: (control.Options ?? [])?.map((option) => ({
        id: option.Id.toString(),
        order: option.Order,
        value: option.Value?.trim()!,
        isHidden: option.IsHidden,
        canBeHidden: option.HasAnswers || option.IsHidden,
        canBeDeleted: !option.HasAnswers && !option.IsHidden,
        canBeReordered: false,
        meta: option,
      })),
    },
    hideTooltip: control.HasAnswers
      ? {
          title: 'Answers provided',
          text: 'This control cannot be deleted, it can only be hidden. Changing the text of the question may lead to data distortion',
        }
      : undefined,
    isHidden: control.IsHidden,
    canBeDeleted: !control.HasAnswers,
    canBeHidden: control.HasAnswers,
    canBeReordered: true,
    initialValue: control.InitialValue?.trim(),
    placeholder: control.Placeholder?.trim() ?? undefined,
    dependencies:
      control.Dependencies?.map((dependency) => ({
        id: dependency.Id.toString(),
        type: mapFormControlDependencyTypeToControlDependencyType(dependency.Type),
        value: dependency.ParentControlTriggerValue,
        parentControlId: dependency.ParentFormControlId.toString(),
        meta: dependency,
      })) ?? [],
    validators:
      control.Validations?.map((validation) => mapValidationToControlValidator(control, validation, allControls)) ?? [],
    meta: {
      ...control,
      Uuid: control.Id.toString(),
      Label: control.Label?.trim()!,
      Info: control.Info
        ? {
            ...control.Info,
            Description: control.Info.Description?.trim(),
          }
        : undefined,
      Options:
        control.Options?.map((option) => ({
          ...option,
          Value: option.Value?.trim()!,
        })) ?? [],
      Attributes:
        control.Attributes?.map((attr) => ({
          ...attr,
          Name: attr.Name?.trim()!,
          Value: attr.Value?.trim()!,
        })) ?? [],
      Dependencies:
        control.Dependencies?.map((dependency) => ({
          ...dependency,
          ParentControlUuid: dependency.ParentFormControlId.toString(),
        })) ?? [],
      Validations:
        control.Validations?.map((validation) => ({
          ...validation,
          DependsOnControlsUuids: validation.ControlIds?.map((id) => id.toString()) ?? [],
        })) ?? [],
    },
  }

  return element
}

// Internal to api

export const mapControlElementTypeToControlType = (
  type: ControlElement['type']
): { ControlType: ControlType; Type: FormControlType } => {
  switch (type) {
    case 'textarea':
      return { ControlType: ControlType.Textarea, Type: FormControlType.Basic }
    case 'checkboxlist':
      return { ControlType: ControlType.Checkboxlist, Type: FormControlType.Basic }
    case 'radiobuttonlist':
      return { ControlType: ControlType.Radiobuttonlist, Type: FormControlType.Basic }
    case 'selectlist':
      return { ControlType: ControlType.Selectlist, Type: FormControlType.Basic }
    case 'date':
      return { ControlType: ControlType.Date, Type: FormControlType.Basic }
    case 'datetime':
      return { ControlType: ControlType.DateTime, Type: FormControlType.Basic }
    case 'text':
      return { ControlType: ControlType.Text, Type: FormControlType.Basic }
    case 'phone':
      return { ControlType: ControlType.Text, Type: FormControlType.PhoneNumber }
    case 'qrcode':
      return { ControlType: ControlType.Text, Type: FormControlType.BarcodeAndQRCode }
    case 'email':
      return { ControlType: ControlType.Text, Type: FormControlType.Email }
    case 'temperature':
      return { ControlType: ControlType.Text, Type: FormControlType.BodyTemperature }
    default:
      throw new Error(`Unknown control type: ${type}`)
  }
}

export const mapControlElementToFormControl = (
  element: ControlElement<FormControlCreateOrUpdateRequest>
): FormControlCreateOrUpdateRequest => {
  const mapControlInfoToRequest = (info: FormControl['Info']): FormControlInfoRequest | undefined => {
    if (!info || (!info.Description && !info.Url)) {
      return undefined
    }

    return {
      Description: info.Description,
      Type: info.Type,
      Url: info.Url,
      FileName: info.FileName,
    }
  }

  return {
    ...(element.meta || {}),
    ...mapControlElementTypeToControlType(element.type),
    Id: element.meta?.Id,
    Uuid: element.id,
    Tooltip: element.tooltip,
    Placeholder: element.placeholder,
    InitialValue: element.initialValue,
    Options: element.options.values.map((option) => ({
      ...(option.meta || {}),
      HasAnswers: false,
      IsHidden: option.isHidden ?? false,
      Value: option.value,
      Order: option.order,
      Conditions: (option.meta as FormControlOptionCreateOrUpdateRequest)?.Conditions,
    })),
    Info: mapControlInfoToRequest(element.meta?.Info),
    IsHidden: element.isHidden ?? false,
    Label: element.name,
    Order: element.order,
    Conditions: element.meta?.Conditions,
    IsRequired: element.isRequired,
    ExternalData: null,
    ProcessByVQAModel: element.meta?.ProcessByVQAModel ?? false,
    Dependencies: element.dependencies.map((dependency) => ({
      Id: (dependency.meta as FormControlDependencyResponse)?.Id,
      Type: mapControlDependencyTypeToFormControlDependencyType(dependency.type),
      ParentControlUuid: dependency.parentControlId,
      ParentControlValue: dependency.value!,
    })),
    Attributes: element.meta?.Attributes ?? [],
    Validations: element.meta?.Validations ?? [],
  }
}

export const mapTabElementToFormTab = (
  element: TabsElement<FormTabsPanelCreateOrUpdateRequest>
): FormTabsPanelCreateOrUpdateRequest => {
  return {
    ...(element.meta || {}),
    Id: element.meta?.Id,
    Name: element.name!,
    Order: element.order,
    Tabs:
      element.meta?.Tabs?.map((tab) => ({
        ...tab,
        Value: tab.Value!,
        Order: tab.Order,
      })) ?? [],
  }
}

export const mapActionElementToFormAction = (
  element: ActionElement<FormActionCreateOrUpdateRequest>
): FormActionCreateOrUpdateRequest => {
  return {
    ...(element.meta || {}),
    Id: element.meta?.Id,
    Label: element.name,
    Order: element.order,
    Tooltip: element.meta?.Tooltip ?? null,
    WorkflowId: element.meta?.WorkflowId!,
  }
}

// if (control.CounterLimit === 0) control.CounterLimit = 1
export const mapCounterElementToFormCounter = (
  element: AICounterElement<FormCounterCreateOrUpdateRequest>
): FormCounterCreateOrUpdateRequest => {
  return {
    ...(element.meta || {}),
    Id: element.meta?.Id,
    CounterLimit: element.meta?.CounterLimit,
    AIObjectId: element.meta?.AIObjectId!,
    CountAIObjects: element.meta?.CountAIObjects ?? false,
    Label: element.meta?.Label!,
    Order: element.order,
    IncrementValue: element.meta?.IncrementValue ?? null,
    IncrementPeriod: element.meta?.IncrementPeriod ?? null,
    Conditions: element.meta?.Conditions ?? [],
  }
}

export const getDefaultControlByControlType = (type: ControlType): FormControlCreateOrUpdateRequest => {
  const control: FormControlCreateOrUpdateRequest = {
    ProcessByVQAModel: false,
    Label: 'New control',
    Order: 0,
    IsRequired: false,
    IsHidden: false,
    ControlType: type,
    Type: FormControlType.Basic,
  }

  if (type === ControlType.Radiobuttonlist || type === ControlType.Selectlist) {
    control.ProcessByVQAModel = true
  }
  if (type === ControlType.Radiobuttonlist || type === ControlType.Selectlist || type === ControlType.Checkboxlist) {
    control.Options = [
      {
        Value: 'Option 1',
        Order: 0,
        IsHidden: false,
      },
      {
        Value: 'Option 2',
        Order: 1,
        IsHidden: false,
      },
      {
        Value: 'Option 3',
        Order: 2,
        IsHidden: false,
      },
    ]
  }

  return control
}

